import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Venue } from '@/types';
import { Building2, Save, X } from 'lucide-react';

interface VenueFormProps {
  venue?: Venue | null;
  onSave: (venue: Partial<Venue>) => Promise<void>;
  onCancel: () => void;
  saving?: boolean;
}

export function VenueForm({ venue, onSave, onCancel, saving = false }: VenueFormProps) {
  const [formData, setFormData] = useState<Partial<Venue>>({
    name: '',
    address: '',
    telephone: '',
    email_address_office: '',
    email_address_dining: '',
    email_address_accounts: '',
    status: 'active',
    notes: '',
    capacity: undefined,
    hasParking: false,
    hasAccessibility: false,
    website: '',
  });

  useEffect(() => {
    if (venue) {
      setFormData({
        name: venue.name || '',
        address: venue.address || '',
        telephone: venue.telephone || '',
        email_address_office: venue.email_address_office || '',
        email_address_dining: venue.email_address_dining || '',
        email_address_accounts: venue.email_address_accounts || '',
        status: venue.status || 'active',
        notes: venue.notes || '',
        capacity: venue.capacity,
        hasParking: venue.hasParking || false,
        hasAccessibility: venue.hasAccessibility || false,
        website: venue.website || '',
      });
    }
  }, [venue]);

  const handleSubmit = async () => {
    await onSave(formData);
  };

  const handleCheckboxChange = (field: 'hasParking' | 'hasAccessibility', checked: boolean) => {
    setFormData((prev) => ({ ...prev, [field]: checked }));
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="name">
          Venue Name <span className="text-destructive">*</span>
        </Label>
        <Input
          id="name"
          value={formData.name}
          onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
          placeholder="Masonic Hall"
          required
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="address">
          Address <span className="text-destructive">*</span>
        </Label>
        <Textarea
          id="address"
          value={formData.address}
          onChange={(e) => setFormData((prev) => ({ ...prev, address: e.target.value }))}
          placeholder="123 High Street, City, County, Postcode"
          rows={3}
          required
        />
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="telephone">Telephone</Label>
          <Input
            id="telephone"
            type="tel"
            value={formData.telephone}
            onChange={(e) => setFormData((prev) => ({ ...prev, telephone: e.target.value }))}
            placeholder="+44 1234 567890"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="website">Website</Label>
          <Input
            id="website"
            type="url"
            value={formData.website}
            onChange={(e) => setFormData((prev) => ({ ...prev, website: e.target.value }))}
            placeholder="https://example.com"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="email_address_office">Office Email</Label>
        <Input
          id="email_address_office"
          type="email"
          value={formData.email_address_office}
          onChange={(e) =>
            setFormData((prev) => ({ ...prev, email_address_office: e.target.value }))
          }
          placeholder="office@example.com"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="email_address_dining">Dining Email</Label>
        <Input
          id="email_address_dining"
          type="email"
          value={formData.email_address_dining}
          onChange={(e) =>
            setFormData((prev) => ({ ...prev, email_address_dining: e.target.value }))
          }
          placeholder="dining@example.com"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="email_address_accounts">Accounts Email</Label>
        <Input
          id="email_address_accounts"
          type="email"
          value={formData.email_address_accounts}
          onChange={(e) =>
            setFormData((prev) => ({ ...prev, email_address_accounts: e.target.value }))
          }
          placeholder="accounts@example.com"
        />
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="capacity">Capacity (Optional)</Label>
          <Input
            id="capacity"
            type="number"
            min="1"
            value={formData.capacity || ''}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                capacity: e.target.value ? parseInt(e.target.value) : undefined,
              }))
            }
            placeholder="120"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="status">
            Status <span className="text-destructive">*</span>
          </Label>
          <Select
            value={formData.status}
            onValueChange={(value: string) =>
              setFormData((prev) => ({ ...prev, status: value as 'active' | 'inactive' }))
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="Select status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            id="hasParking"
            checked={formData.hasParking || false}
            onChange={(e) => handleCheckboxChange('hasParking', e.target.checked)}
            className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
          />
          <Label htmlFor="hasParking" className="font-normal cursor-pointer">
            Has Parking
          </Label>
        </div>

        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            id="hasAccessibility"
            checked={formData.hasAccessibility || false}
            onChange={(e) => handleCheckboxChange('hasAccessibility', e.target.checked)}
            className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
          />
          <Label htmlFor="hasAccessibility" className="font-normal cursor-pointer">
            Has Accessibility Features
          </Label>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="notes">Internal Notes</Label>
        <Textarea
          id="notes"
          value={formData.notes}
          onChange={(e) => setFormData((prev) => ({ ...prev, notes: e.target.value }))}
          placeholder="Any internal notes about this venue..."
          rows={3}
        />
      </div>

      <div className="flex justify-end gap-2 pt-4">
        <Button type="button" variant="outline" onClick={onCancel} disabled={saving}>
          <X className="h-4 w-4 mr-2" />
          Cancel
        </Button>
        <Button type="button" onClick={handleSubmit} disabled={saving}>
          <Save className="h-4 w-4 mr-2" />
          {saving ? 'Saving...' : venue ? 'Update Venue' : 'Create Venue'}
        </Button>
      </div>
    </div>
  );
}

